import AppIconButton from "../../../components/form/AppIconButton";
import { useState, useEffect } from "react";

// react-router components
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

import Breadcrumbs from "../../Breadcrumbs";
import { LanguageSelect, ThemeModeToggle } from "../../../components/AppPreferences";
import HelpButton from "../../../components/HelpButton";
import CompactActionsMenu from "./CompactActionsMenu";
import UserAccountMenu from "./UserAccountMenu";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";

import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "../../../context";

import authService from "../../../services/authService";

function DashboardNavbar({ absolute = false, light = false, isMini = false, hidden = false }) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const navbar = custom.widgets.navbar;
  const [navbarType, setNavbarType] = useState<"static" | "sticky">("static");
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState<any>(null);
  // Phones and tablets (below the sidenav breakpoint): one row, controls collapsed into a single menu.
  const compact = useMediaQuery(useTheme().breakpoints.down("lg"));

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const userInfo = await authService.getUser();
        setUser(userInfo);
      } catch (error) {
        console.error("Error getting user info:", error);
      }
    };

    getUserInfo();
  }, []);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => {
    setMiniSidenav(dispatch, !miniSidenav);
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  if (hidden) {
    return null;
  }

  const userLabel: string =
    user?.profile?.preferred_username ||
    user?.profile?.name ||
    user?.profile?.email ||
    t("navbar.defaultUser");
  const userEmail: string | undefined = user?.profile?.email;
  const navbarState = { transparentNavbar, absolute, light, darkMode };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={navbar.appBar(navbarState)}
    >
      <Toolbar sx={navbar.toolbar}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            gap: 0.75,
            minWidth: 0,
            flex: 1,
            py: 0.25,
          }}
        >
          <AppIconButton
            disableRipple
            color="inherit"
            aria-label={miniSidenav ? t("navbar.openMenu") : t("navbar.closeMenu")}
            sx={navbar.mobileMenuButton}
            onClick={handleMiniSidenav}
          >
            <Icon sx={navbar.iconColor(darkMode)} fontSize="medium">
              {isMobile ? (miniSidenav ? "menu" : "close") : miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </AppIconButton>
          <Box color="inherit" sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, alignSelf: "stretch" }}>
            <Breadcrumbs
              icon="explore"
              title={route[route.length - 1]}
              route={route}
              light={darkMode}
              darkMode={darkMode}
            />
          </Box>
        </Box>
        {compact ? (
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <CompactActionsMenu iconSx={navbar.iconColor(darkMode)} />
            {user ? (
              <UserAccountMenu
                userLabel={userLabel}
                userEmail={userEmail}
                avatarSx={navbar.userAvatar}
                showDetails={false}
              />
            ) : null}
            <AppIconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbar.iconButton}
              aria-label={t("navbar.settings")}
              onClick={handleConfiguratorOpen}
            >
              <Icon sx={navbar.iconColor(darkMode)}>settings</Icon>
            </AppIconButton>
          </Box>
        ) : (
          <Box sx={navbar.row({ isMini })}>
            <Box color={light ? "white" : "inherit"} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <LanguageSelect />
              <ThemeModeToggle />
              <HelpButton iconSx={navbar.iconColor(darkMode)} />
              {user ? (
                <UserAccountMenu
                  userLabel={userLabel}
                  userEmail={userEmail}
                  avatarSx={navbar.userAvatar}
                  userNameSx={navbar.userName(darkMode)}
                  userEmailSx={navbar.userEmail(darkMode)}
                  showDetails
                />
              ) : null}
              <AppIconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbar.iconButton}
                aria-label={t("navbar.settings")}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={navbar.iconColor(darkMode)}>settings</Icon>
              </AppIconButton>
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  hidden: PropTypes.bool,
};

export default DashboardNavbar;
